import { infiniteQueryOptions, keepPreviousData } from '@tanstack/react-query';

import { ONE_MINUTE } from '@/constants/time';
import { getInboxPage, type InboxGrouping, type InboxPage } from '@/services/conversations/inbox';
import { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

export type InquiryV2 =
  paths['/v2/inquiries']['get']['responses'][200]['content']['application/json']['data']['inquiries'][0];

type GetInquiriesV2Params = Exclude<
  paths['/v2/inquiries']['get']['parameters']['query'],
  undefined
>;

export type GetInquiriesV2Response = InboxPage;

const DEFAULT_LIMIT = 10;

export async function getInquiriesV2({
  page = 1,
  limit = DEFAULT_LIMIT,
  cancerTypeId,
  conversationState,
}: GetInquiriesV2Params = {}) {
  const { data } = await fetchClient.GET('/v2/inquiries', {
    params: { query: { page, limit, cancerTypeId, conversationState } },
  });

  return data?.data;
}

type GetInquiriesV2InfiniteQueryOptionsParams = Pick<
  NonNullable<Parameters<typeof getInboxPage>[0]>,
  'limit' | 'cancerTypeId' | 'conversationState'
>;

/**
 * An inbox tab: inquiries and support threads together, from the inbox API,
 * which owns their order, tab membership and cancer filtering. The query key
 * and the page's `inquiries` keep their inquiry-list meaning, so every patch
 * written against an inquiry list still applies.
 *
 * `grouping: 'question'` asks the server to group by question. It adds a fifth key element, so a
 * grouped list and a flat one never share a cache entry, and every flat caller's key is exactly
 * what it was. Prefix matches on the flat key still reach the grouped one.
 */
export function getInquiriesV2InfiniteQueryOptions(
  params?: GetInquiriesV2InfiniteQueryOptionsParams & {
    userId?: string;
    grouping?: InboxGrouping;
  }
) {
  const {
    limit = DEFAULT_LIMIT,
    cancerTypeId,
    conversationState,
    userId,
    grouping = 'none',
  } = params || {};

  const queryKeyState = conversationState === 'active' ? 'open' : conversationState;

  const isGrouped = grouping === 'question';

  return infiniteQueryOptions({
    queryKey: isGrouped
      ? ['inquiriesV2', queryKeyState, userId, cancerTypeId, grouping]
      : ['inquiriesV2', queryKeyState, userId, cancerTypeId],
    queryFn: ({ pageParam }) =>
      getInboxPage(
        {
          cursor: pageParam,
          limit,
          cancerTypeId,
          conversationState,
          ...(isGrouped && { groupBy: grouping }),
        },
        userId
      ),
    enabled: !!userId,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: ONE_MINUTE,
    gcTime: ONE_MINUTE,
  });
}

export function getAllInquiriesV2QueryOptions(
  userId?: string,
  cancerTypeId?: number,
  grouping?: InboxGrouping
) {
  return getInquiriesV2InfiniteQueryOptions({
    userId,
    cancerTypeId,
    grouping,
  });
}

export function getOpenInquiriesV2QueryOptions(
  userId?: string,
  cancerTypeId?: number,
  grouping?: InboxGrouping
) {
  return getInquiriesV2InfiniteQueryOptions({
    userId,
    cancerTypeId,
    conversationState: 'active',
    grouping,
  });
}

export function getClosedInquiriesV2QueryOptions(
  userId?: string,
  cancerTypeId?: number,
  grouping?: InboxGrouping
) {
  return getInquiriesV2InfiniteQueryOptions({
    userId,
    cancerTypeId,
    conversationState: 'closed',
    grouping,
  });
}

export function getAllClosedInquiriesV2QueryOptions(userId?: string, cancerTypeId?: number) {
  return {
    queryKey: ['allClosedInquiriesV2', userId, cancerTypeId],
    queryFn: async () => {
      const fetchPage = async (
        pageNumber: number,
        accumulatedInquiries: InquiryV2[] = []
      ): Promise<InquiryV2[]> => {
        const result = await getInquiriesV2({
          page: pageNumber,
          limit: 50,
          cancerTypeId,
          conversationState: 'closed',
        });

        if (!result) {
          return accumulatedInquiries;
        }

        const currentPageInquiries = result.inquiries ?? [];

        const allInquiriesSoFar = [...accumulatedInquiries, ...currentPageInquiries];

        const hasMorePages = result.currentPage < result.totalPages;

        return hasMorePages ? fetchPage(pageNumber + 1, allInquiriesSoFar) : allInquiriesSoFar;
      };

      const allInquiries = await fetchPage(1);

      return allInquiries;
    },
    enabled: !!userId,
    staleTime: ONE_MINUTE,
    gcTime: ONE_MINUTE,
  };
}
